import { useCallback, useMemo, useEffect } from "react";
import { useForm } from "react-hook-form";
import { object, string } from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { digitsOnlyPhone } from "../../../../util/phoneNumber";
import { isWhatsAppMsPlatform } from "../../../../services/inbox/inboxMsChatPlatform";

/**
 * True when phone has a usable value (rejects empty, whitespace, or lone "+").
 * Matches the inbox/orders format: optional leading "+" followed by digits.
 */
const isValidPhoneValue = (phone) => {
  const value = String(phone || '').trim();
  if (!value) return false;
  return /^\+?\d+$/.test(value) && digitsOnlyPhone(value).length > 0;
};

/**
 * Keeps only valid phones; invalid values like "+" become empty so customers
 * identified by user_id can still be saved without a phone.
 */
const normalizePhoneValue = (phone) => (
  isValidPhoneValue(phone) ? String(phone).trim() : ''
);

/**
 * Country-agnostic: the backend already strips the dial code into
 * `phone_without_country_code`. If that national number is missing/invalid,
 * the customer has no usable phone (e.g. WhatsApp user_id-only contacts).
 */
const hasUsableInboxPhone = (customer) => {
  if (!customer) return false;

  if (customer.hasPhone === false) return false;

  const nationalPhone = customer.phone_without_country_code;
  if (nationalPhone == null || String(nationalPhone).trim() === '') {
    return false;
  }

  return isValidPhoneValue(nationalPhone);
};

/**
 * @typedef {Object} CustomerData
 * @property {string} [id] - ID of the customer (optional for new customers)
 * @property {string} [first_name] - First name of the customer
 * @property {string} [last_name] - Last name of the customer
 * @property {string} [email] - Email of the customer (optional)
 * @property {string} [phone] - Phone of the customer (optional)
 * @property {string} [country_id] - ID of the country of the customer (optional)
 * @property {string} [notes] - Notes of the customer (optional)
 * @property {string} [id_type] - ID type of the customer (optional)
 * @property {string} [id_number] - ID number of the customer (optional)
 */

/**
 * @typedef {Object} CustomField
 * @property {string} id - ID of the custom field
 * @property {string} identifier - Unique identifier of the custom field
 * @property {string} name - Name of the custom field
 * @property {string} field_type - Type of the custom field (string, integer, float, date, boolean, list)
 * @property {string} [answer] - Current value of the custom field
 * @property {string} [id_saved] - ID of the saved record for update
 * @property {Array<Object>} [list_options] - Options for list fields
 */

/**
 * @typedef {Object} FormMethods
 * @property {function} control - Control de react-hook-form
 * @property {function} handleSubmit - Function to handle form submission
 * @property {function} reset - Function to reset the form
 * @property {Object} formState - State of the form (isValid, errors, isDirty, etc.)
 */

/**
 * @typedef {Object} TransformedFormData
 * @property {string} [id] - ID del cliente
 * @property {string} first_name - First name of the customer
 * @property {string} last_name - Last name of the customer
 * @property {string} [email] - Email of the customer
 * @property {string} [phone] - Phone of the customer
 * @property {string} [country_id] - ID of the country of the customer
 * @property {string} [notes] - Notes of the customer
 * @property {string} [id_type] - ID type of the customer
 * @property {string} [id_number] - ID number of the customer
 * @property {string} platform - Platform of the conversation (whatsapp, messenger, instagram)
 * @property {Array<CustomerRelatedDataAttribute>} customer_related_data_attributes - Campos personalizados transformados
 */

/**
 * @typedef {Object} CustomerRelatedDataAttribute
 * @property {string} customer_related_field_id - ID of the custom field
 * @property {string} data - Value of the field (formatted according to type)
 * @property {string} [id] - ID del registro existente para actualización
 */

/**
 * @typedef {Object} UseCustomerDetailFormReturn
 * @property {FormMethods} formMethods - Methods of react-hook-form
 * @property {function(Object): TransformedFormData} transformFormData - Function to transform form data
 * @property {Object} validationSchema - Validation schema of Yup
 * @property {boolean} isFormValid - Indicates if the form is valid
 * @property {Object} formErrors - Validation errors
 * @property {boolean} isDirty - Indicates if the form has been modified
 */

/**
 * Hook de Form Layer for the management of the form of CustomerDetailFormMC
 *
 * @hook
 * @category Form Layer
 * @subcategory Customer Management
 *
 * @description
 * Hook specialized to manage the state of the form of customer details.
 * Implements dynamic validation with Yup, handling of custom fields and
 * transformación de datos para la API backend. Incluye información de plataforma
 * para actualizaciones de conversación en Redux store.
 *
 * @features
 * - ✅ Gestión completa del formulario con React Hook Form
 * - ✅ Dynamic validation schema based on custom fields
 * - ✅ Automatic data transformation for API backend
 * - ✅ Inclusión automática de plataforma en datos transformados
 * - ✅ Valores por defecto automáticos y actualizables
 * - ✅ Validación en tiempo real con manejo de errores
 * - ✅ Support for multiple types of custom fields
 * - ✅ Date formatting and specific validations by type
 *
 * @responsibilities
 * ✅ Management of the form state (react-hook-form)
 * ✅ Validation of fields (dynamic Yup schema)
 * ✅ Default values of the form
 * ✅ Data transformation for sending to API
 * ✅ Inclusion of platform information in transformed data
 * ✅ Dynamic field configuration
 * ✅ Handling of custom fields by type
 *
 * @restrictions
 * ❌ API calls (that goes in Actions Layer)
 * ❌ API loading/error state
 * ❌ UI presentation logic
 * ❌ Visual component configurations
 *
 * @performance
 * - useMemo for dynamic validation schema
 * - useCallback for transformation functions
 * - useEffect for automatic value updates
 * - Memoization of the return value
 *
 * @param {CustomerData} customer - Customer data to edit
 * @param {Array<CustomField>} customFieldsGrouped - Custom fields grouped and processed
 * @param {string} platform - Platform of the conversation (whatsapp, messenger, instagram)
 *
 * @since 3.0.0
 * @version 3.1.0
 *
 * @returns {UseCustomerDetailFormReturn} Object with form methods and memoized utilities
 */
function useCustomerDetailForm(customer, customFieldsGrouped, platform) {
  /**
   * Function for translation of form messages
   * @type {function}
   */
  const { t } = useTranslation('InboxMC');

  /**
   * Phone/country are editable outside WhatsApp, or on WhatsApp when there is no
   * national phone (user_id-only, empty phone, or "+"). Works for any country
   * because it relies on `phone_without_country_code`, not a digit-length guess.
   * isWhatsAppMsPlatform covers legacy `whatsapp` and MS multinúmero enums.
   */
  const shouldShowPhoneFields = !isWhatsAppMsPlatform(platform) || !hasUsableInboxPhone(customer);

  /**
   * Dynamic validation schema memoized
   *
   * @type {Object}
   * @memoized
   *
   * @description
   * Validation schema that is built dynamically based on the available custom fields.
   * Includes base validations for standard fields and type-specific custom fields.
   *
   * @cross_validation
   * - country_id <- phone: country required when a valid phone is present (only if phone fields are editable)
   * - phone: optional, but if present must be a valid format (not just "+")
   * - WhatsApp with valid phone: phone/country fields hidden; cross-validation skipped
   */
  const validationSchema = useMemo(() => {
    // Base schema for static fields
    const baseShape = {
      first_name: string()
        .required(t('customerDetails.errors.fieldRequired', { field: t('customerDetails.firstName') }))
        .trim()
        .max(100, t('customerDetails.errors.firstNameTooLong')),
      last_name: string()
        .required(t('customerDetails.errors.fieldRequired', { field: t('customerDetails.lastName') }))
        .trim()
        .max(100, t('customerDetails.errors.lastNameTooLong')),
      country_id: string()
        .test('country-phone-validation', t('customerDetails.errors.countryPhoneRequired'), function (value) {
          if (!shouldShowPhoneFields) return true;

          const { phone } = this.parent;
          // If there is a valid phone, there must be a country (legacy inbox rule)
          if (isValidPhoneValue(phone) && (!value || !String(value).trim())) {
            return false;
          }
          return true;
        })
        .nullable(),
      phone: string()
        .trim()
        .nullable()
        .test('is-valid-phone', t('customerDetails.errors.invalidPhone'), function (value) {
          if (!shouldShowPhoneFields) return true;
          if (!value || !String(value).trim()) return true;

          return isValidPhoneValue(value);
        }),
      email: string().email(t('customerDetails.errors.invalidEmail')).nullable(),
      id_type: string().nullable(),
      id_number: string().trim().max(50, t('customerDetails.errors.idTooLong')),
      notes: string().trim().max(1000, t('customerDetails.errors.notesTooLong'))
    };

    // Dynamic schema for custom fields
    const dynamicShape = {};

    if (customFieldsGrouped && customFieldsGrouped.length > 0) {
      customFieldsGrouped.forEach((field) => {
        const fieldPath = `related_data.${field.identifier}`;

        switch (field.field_type) {
          case "string":
            dynamicShape[fieldPath] = string()
              .trim()
              .max(500, t('customerDetails.errors.fieldTooLong', { field: field.name, max: 500 }))
              .nullable();
            break;

          case "integer":
            dynamicShape[fieldPath] = string()
              .matches(/^(\d+)?$/, t('customerDetails.errors.mustBeInteger', { field: field.name }))
              .nullable();
            break;

          case "float":
            dynamicShape[fieldPath] = string()
              .matches(/^(\d*\.?\d+)?$/, t('customerDetails.errors.mustBeNumber', { field: field.name }))
              .nullable();
            break;

          case "date":
            dynamicShape[fieldPath] = string().nullable();
            break;

          case "boolean":
            // Boolean fields do not need additional validation
            break;

          case "list":
            dynamicShape[fieldPath] = string().nullable();
            break;

          default:
            dynamicShape[fieldPath] = string().nullable();
        }
      });
    }

    return object().shape({
      ...baseShape,
      related_data: object().shape(
        Object.keys(dynamicShape).reduce((acc, fullPath) => {
          const fieldName = fullPath.replace('related_data.', '');
          acc[fieldName] = dynamicShape[fullPath];
          return acc;
        }, {})
      )
    });
  }, [t, customFieldsGrouped, shouldShowPhoneFields]);

  /**
   * Memoized function to get default values of the form
   *
   * @function
   * @memoized
   *
   * @description
   * Generates the initial values of the form based on the customer data
   * and the available custom fields. It is automatically updated
   * when the customer data or the custom fields change.
   *
   * @returns {Object} Object with default values for all fields
   *
   * @structure
   * - Static fields: first_name, last_name, email, phone, etc.
   * - related_data: object with custom fields grouped by identifier
   */
  const getDefaultValues = useCallback(() => {
    const usablePhone = hasUsableInboxPhone(customer);
    // Prefer national number when editing with country dropdown; clear unusable values
    const phoneDefault = usablePhone
      ? normalizePhoneValue(customer?.phone_without_country_code || customer?.phone)
      : '';

    return {
      id: customer?.id || '',
      first_name: customer?.first_name || '',
      last_name: customer?.last_name || '',
      country_id: customer?.country_id || '',
      phone: phoneDefault,
      email: customer?.email || '',
      notes: customer?.notes || '',
      id_type: customer?.id_type || '',
      id_number: customer?.id_number || '',
      // Add custom fields if they exist
      related_data: customFieldsGrouped?.reduce((acc, field) => {
        acc[field.identifier] = field.answer || '';
        return acc;
      }, {}) || {}
    };
  }, [customer, customFieldsGrouped]);

  /**
   * React Hook Form instance configured with validation and default values
   *
   * @type {FormMethods}
   *
   * @description
   * Configuration of react-hook-form with:
   * - Yup resolver for validation
   * - Dynamic default values
   * - Real-time validation mode (onChange)
   *
   * @configuration
   * - resolver: yupResolver with dynamic schema
   * - defaultValues: values calculated from customer and custom fields
   * - mode: 'onChange' for real-time validation
   */
  const formMethods = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: getDefaultValues(),
    mode: 'onChange' // Validación en tiempo real
  });

  /**
   * Effect to update form values when the customer data or the custom fields change
   *
   * @effect
   *
   * @description
   * Automatically updates the form values when:
   * - The customer data changes
   * - The custom fields are modified
   * - The default values are recalculated
   *
   * @dependencies [getDefaultValues, formMethods]
   */
  useEffect(() => {
    if (formMethods.formState.isDirty) return;
    const newValues = getDefaultValues();
    formMethods.reset(newValues);
  }, [getDefaultValues, formMethods, formMethods.formState.isDirty]);

  /**
   * Memoized function to transform form data to the API format
   *
   * @function
   * @memoized
   *
   * @param {Object} formData - Form data from react-hook-form
   * @returns {TransformedFormData} Transformed data ready to send to API
   *
   * @description
   * Transforms the form data to the expected format by the API backend:
   * - Separates base fields from custom fields
   * - Formats custom fields according to their type
   * - Applies specific formatting (dates, numbers, etc.)
   * - Includes IDs for existing field updates
   * - Adds platform information for conversation context
   *
   * @transformation_rules
   * - date: format YYYY-MM-DD using moment.js
   * - platform: included automatically in base data
   * - integer/float: keeps as string for validation
   * - boolean: direct value
   * - string: direct value or empty string
   * - list: selected value from options
   */
  const transformFormData = useCallback((formData) => {
    const { related_data, ...data } = formData;
    const transformedData = {
      ...data,
      phone: normalizePhoneValue(data.phone),
      platform
    };
    transformedData.customer_related_data_attributes = [];

    if (related_data && customFieldsGrouped) {
      customFieldsGrouped.forEach((field) => {
        const fieldValue = related_data[field.identifier];

        const customer_related_data = {
          customer_related_field_id: field.id
        };

        switch (field.field_type) {
          case 'date':
            // Validate and format date
            customer_related_data.data = fieldValue && fieldValue.trim()
              ? moment(fieldValue).format("YYYY-MM-DD")
              : '';
            break;
          default:
            customer_related_data.data = fieldValue || '';
        }

        // If there is a saved ID, include it for update
        if (field.id_saved) {
          customer_related_data.id = field.id_saved;
        }

        transformedData.customer_related_data_attributes.push(customer_related_data);
      });
    }

    return transformedData;
  }, [customFieldsGrouped, platform]);

  /**
   * Memoized return value of the hook
   *
   * @type {UseCustomerDetailFormReturn}
   * @memoized
   *
   * @description
   * Memoized object that contains all the utilities of the form.
   * It is re-memoized only when the dependencies change to optimize renders.
   *
   * @properties
   * @property {FormMethods} formMethods - Complete react-hook-form methods
   * @property {function} transformFormData - Function to transform data
   * @property {Object} validationSchema - Yup validation schema
   * @property {boolean} isFormValid - Validation state of the form
   * @property {Object} formErrors - Validation errors
   * @property {boolean} isDirty - Indicates if the form has been modified
   */
  const returnValue = useMemo(() => ({
    formMethods,
    transformFormData,
    validationSchema,
    shouldShowPhoneFields,
    isFormValid: formMethods.formState.isValid,
    formErrors: formMethods.formState.errors,
    isDirty: formMethods.formState.isDirty
  }), [
    formMethods,
    transformFormData,
    validationSchema,
    shouldShowPhoneFields,
    formMethods.formState.isValid,
    formMethods.formState.errors,
    formMethods.formState.isDirty
  ]);

  return returnValue;
}

export default useCustomerDetailForm;
